import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Avatar, Box, Button, Card, CardContent, Chip, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControl, FormControlLabel,
  FormGroup, Grid, IconButton, InputAdornment, InputLabel, LinearProgress,
  MenuItem, Select, Snackbar, Stack, Switch, TextField, Tooltip,
  Typography, Alert, Checkbox, useTheme, alpha
} from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import {
  Add, Delete, Edit, Refresh, LockReset, Security, Search,
  PersonAdd, AdminPanelSettings, SupportAgent, People, Block,
  CheckCircle, Cancel, MoreVert, Shield, Visibility, VisibilityOff,
  ContentCopy, Email
} from '@mui/icons-material';
import { UserContext } from '../../../contexts/UserContext';
import { fetchJson } from '../../../utils/fetchJson';
import ConfirmDeleteDialog from '../../../components/ConfirmDeleteDialog';

const ROLE_OPTIONS = [
  { value: 'SystemAdmin', label: 'System Admin', icon: <AdminPanelSettings fontSize="small" />, color: '#7c3aed' },
  { value: 'ITManager', label: 'IT Manager', icon: <Shield fontSize="small" />, color: '#2563eb' },
  { value: 'SalesRep', label: 'Sales Rep', icon: <People fontSize="small" />, color: '#059669' },
  { value: 'SupportAgent', label: 'Support Agent', icon: <SupportAgent fontSize="small" />, color: '#d97706' }
];

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'success', icon: <CheckCircle fontSize="small" /> },
  inactive: { label: 'Inactive', color: 'default', icon: <Cancel fontSize="small" /> },
  suspended: { label: 'Suspended', color: 'error', icon: <Block fontSize="small" /> }
};

const SYSTEM_PAGES = [
  {
    key: 'system-overview',
    label: 'Overview / Dashboard',
    features: [
      { key: 'system-overview:view', label: 'View dashboard insights' },
      { key: 'system-overview:export', label: 'Export reports' }
    ]
  },
  {
    key: 'schools',
    label: 'School Directory',
    features: [
      { key: 'schools:view', label: 'View schools' },
      { key: 'schools:create', label: 'Create schools' },
      { key: 'schools:edit', label: 'Edit schools' },
      { key: 'schools:delete', label: 'Delete schools' }
    ]
  },
  {
    key: 'registration-queue',
    label: 'Registration Queue',
    features: [
      { key: 'registration-queue:view', label: 'View registrations' },
      { key: 'registration-queue:review', label: 'Approve / reject registrations' }
    ]
  },
  {
    key: 'onboarding-status',
    label: 'Onboarding Status',
    features: [
      { key: 'onboarding-status:view', label: 'View onboarding progress' },
      { key: 'onboarding-status:update', label: 'Update onboarding steps' }
    ]
  },
  {
    key: 'billing',
    label: 'Billing & Subscriptions',
    features: [
      { key: 'billing:view', label: 'View billing records' },
      { key: 'billing:manage', label: 'Manage subscriptions' },
      { key: 'billing:refund', label: 'Process refunds / adjustments' }
    ]
  },
  {
    key: 'users-access',
    label: 'Users & Access',
    features: [
      { key: 'users-access:view', label: 'View system users' },
      { key: 'users-access:create', label: 'Create system users' },
      { key: 'users-access:edit', label: 'Edit system users' },
      { key: 'users-access:delete', label: 'Delete system users' },
      { key: 'users-access:permissions', label: 'Manage permissions' }
    ]
  },
  {
    key: 'system-settings',
    label: 'Platform Settings',
    features: [
      { key: 'system-settings:view', label: 'View platform settings' },
      { key: 'system-settings:edit', label: 'Edit platform settings' }
    ]
  },
  {
    key: 'communications',
    label: 'Communications',
    features: [
      { key: 'communications:view', label: 'View communications' },
      { key: 'communications:send', label: 'Send broadcasts / notices' }
    ]
  },
  {
    key: 'security',
    label: 'Security & Audit',
    features: [
      { key: 'security:view', label: 'View security logs' },
      { key: 'security:manage', label: 'Manage security settings' }
    ]
  },
  {
    key: 'support-tickets',
    label: 'Support Tickets',
    features: [
      { key: 'support-tickets:view', label: 'View support tickets' },
      { key: 'support-tickets:manage', label: 'Respond / resolve tickets' }
    ]
  },
  {
    key: 'integrations',
    label: 'Integrations',
    features: [
      { key: 'integrations:view', label: 'View integrations' },
      { key: 'integrations:manage', label: 'Configure integrations' }
    ]
  },
  {
    key: 'it-services',
    label: 'IT Services',
    features: [
      { key: 'it-services:dashboard', label: 'IT dashboard' },
      { key: 'it-services:monitoring', label: 'System monitoring' },
      { key: 'it-services:backup', label: 'Backup management' },
      { key: 'it-services:security', label: 'Security audit' },
      { key: 'it-services:api', label: 'API management' }
    ]
  }
];

const DEFAULT_ROLE_PERMISSIONS = {
  SystemAdmin: ['*'],
  ITManager: [
    'system-overview', 'system-overview:view', 'system-overview:export',
    'system-settings', 'system-settings:view', 'system-settings:edit',
    'security', 'security:view', 'security:manage',
    'integrations', 'integrations:view', 'integrations:manage',
    'it-services', 'it-services:dashboard', 'it-services:monitoring', 'it-services:backup', 'it-services:security', 'it-services:api'
  ],
  SalesRep: [
    'system-overview', 'system-overview:view',
    'schools', 'schools:view', 'schools:create', 'schools:edit',
    'registration-queue', 'registration-queue:view',
    'onboarding-status', 'onboarding-status:view', 'onboarding-status:update'
  ],
  SupportAgent: [
    'system-overview', 'system-overview:view',
    'registration-queue', 'registration-queue:view', 'registration-queue:review',
    'onboarding-status', 'onboarding-status:view',
    'support-tickets', 'support-tickets:view', 'support-tickets:manage',
    'communications', 'communications:view'
  ]
};

function getDefaultPermissions(role) {
  return [...(DEFAULT_ROLE_PERMISSIONS[role] || [])];
}

function isPermissionSelected(permissions, key, role) {
  return role === 'SystemAdmin' || permissions.includes('*') || permissions.includes(key);
}

function getPageFeatureKeys(pageKey) {
  return (SYSTEM_PAGES.find(pg => pg.key === pageKey)?.features || []).map(ft => ft.key);
}

function togglePagePermissionSet(currentPermissions, role, pageKey) {
  if (role === 'SystemAdmin') return ['*'];
  const featureKeys = getPageFeatureKeys(pageKey);
  const hasPage = currentPermissions.includes(pageKey);
  if (hasPage) {
    return currentPermissions.filter(key => key !== pageKey && !featureKeys.includes(key));
  }
  return Array.from(new Set([...currentPermissions, pageKey, ...featureKeys]));
}

function toggleFeaturePermissionSet(currentPermissions, role, pageKey, featureKey) {
  if (role === 'SystemAdmin') return ['*'];
  if (currentPermissions.includes(featureKey)) {
    return currentPermissions.filter(key => key !== featureKey);
  }
  return Array.from(new Set([...currentPermissions, pageKey, featureKey]));
}

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').filter(Boolean).map(w => w[0]).slice(0, 2).join('').toUpperCase();
}

function getRoleConfig(role) {
  return ROLE_OPTIONS.find(r => r.value === role) || { label: role || 'Unknown', color: '#666', icon: <People fontSize="small" /> };
}

function timeAgo(dateStr) {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

export default function UsersAccessPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const currentUser = useContext(UserContext);

  const canFeature = useCallback((permKey) => {
    if (currentUser?.role === 'SystemAdmin') return true;
    const perms = currentUser && Array.isArray(currentUser.systemPermissions) ? currentUser.systemPermissions : null;
    if (!perms) return true;
    if (perms.includes('*')) return true;
    return perms.includes(permKey);
  }, [currentUser]);

  const canManageUsersAccess = canFeature('users-access');

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [filters, setFilters] = useState({ role: '', status: '', search: '' });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  const [snack, setSnack] = useState({ open: false, severity: 'success', message: '' });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', role: 'ITManager', status: 'active' });
  const [showPassword, setShowPassword] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [formPermissions, setFormPermissions] = useState(getDefaultPermissions('ITManager'));

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [permDialogOpen, setPermDialogOpen] = useState(false);
  const [permUser, setPermUser] = useState(null);
  const [userPermissions, setUserPermissions] = useState([]);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailUser, setDetailUser] = useState(null);

  // ---------- Data loading ----------
  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.set('page', String(page + 1));
      params.set('pageSize', String(pageSize));
      if (filters.role) params.set('role', filters.role);
      if (filters.status) params.set('status', filters.status);
      if (filters.search) params.set('search', filters.search);
      const data = await fetchJson(`/api/system-users?${params.toString()}`);
      setRows(Array.isArray(data?.rows) ? data.rows : []);
      setTotal(Number.isFinite(data?.total) ? data.total : 0);
    } catch (e) {
      setError(e?.message || 'Failed to load system users');
    } finally {
      setLoading(false);
    }
  }, [filters.role, filters.search, filters.status, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  // ---------- Summary stats ----------
  const stats = useMemo(() => {
    const all = rows;
    return {
      total,
      active: all.filter(r => r.status === 'active').length,
      inactive: all.filter(r => r.status === 'inactive').length,
      suspended: all.filter(r => r.status === 'suspended').length,
      admins: all.filter(r => r.role === 'SystemAdmin').length,
      recentlyActive: all.filter(r => {
        if (!r.lastLoginAt) return false;
        return (Date.now() - new Date(r.lastLoginAt).getTime()) < 24 * 60 * 60 * 1000;
      }).length
    };
  }, [rows, total]);

  // ---------- CRUD ----------
  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', email: '', phone: '', role: 'ITManager', status: 'active' });
    setFormPermissions(getDefaultPermissions('ITManager'));
    setGeneratedPassword('');
    setShowPassword(false);
    setDialogOpen(true);
  };

  const openEdit = async (row) => {
    setEditing(row);
    setForm({
      name: row?.name || '',
      email: row?.email || '',
      phone: row?.phone || '',
      role: row?.role || 'ITManager',
      status: row?.status || 'active'
    });
    try {
      const data = await fetchJson(`/api/system-users/${row.id}/permissions`);
      setFormPermissions(row?.role === 'SystemAdmin'
        ? ['*']
        : (Array.isArray(data?.permissions) ? data.permissions : getDefaultPermissions(row?.role || 'ITManager')));
    } catch (_) {
      setFormPermissions(getDefaultPermissions(row?.role || 'ITManager'));
    }
    setGeneratedPassword('');
    setShowPassword(false);
    setDialogOpen(true);
  };

  const save = async () => {
    setError('');
    try {
      if (!form.name) throw new Error('Name is required');
      if (!form.email) throw new Error('Email is required');
      if (!form.role) throw new Error('Role is required');

      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        role: form.role,
        status: form.status,
        permissions: form.role === 'SystemAdmin' ? ['*'] : formPermissions
      };

      if (editing?.id) {
        await fetchJson(`/api/system-users/${editing.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: payload.name, email: payload.email, phone: payload.phone, role: payload.role, status: payload.status })
        });
        if (form.role !== 'SystemAdmin') {
          await fetchJson(`/api/system-users/${editing.id}/permissions`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ permissions: formPermissions })
          });
        }
        setSnack({ open: true, severity: 'success', message: 'System user updated' });
      } else {
        await fetchJson('/api/system-users', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        setSnack({ open: true, severity: 'success', message: 'System user created. Credentials sent via email.' });
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      setSnack({ open: true, severity: 'error', message: e?.message || 'Failed to save' });
    }
  };

  const setStatus = async (row, status) => {
    try {
      await fetchJson(`/api/system-users/${row.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      setSnack({ open: true, severity: 'success', message: `User ${status === 'active' ? 'activated' : status === 'suspended' ? 'suspended' : 'deactivated'}` });
      await load();
    } catch (e) {
      setSnack({ open: true, severity: 'error', message: e?.message || 'Failed to update status' });
    }
  };

  const resetPassword = async (row) => {
    try {
      await fetchJson(`/api/system-users/${row.id}/reset-password`, { method: 'POST' });
      setSnack({ open: true, severity: 'success', message: 'Password reset. New credentials sent via email.' });
    } catch (e) {
      setSnack({ open: true, severity: 'error', message: e?.message || 'Failed to reset password' });
    }
  };

  const askDelete = (row) => { setDeleteTarget(row); setDeleteOpen(true); };
  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      await fetchJson(`/api/system-users/${deleteTarget.id}`, { method: 'DELETE' });
      setSnack({ open: true, severity: 'success', message: 'User deleted' });
      setDeleteOpen(false);
      setDeleteTarget(null);
      await load();
    } catch (e) {
      setSnack({ open: true, severity: 'error', message: e?.message || 'Failed to delete' });
      setDeleteOpen(false);
      setDeleteTarget(null);
    }
  };

  // ---------- Permissions ----------
  const openPermissions = async (row) => {
    setPermUser(row);
    try {
      const data = await fetchJson(`/api/system-users/${row.id}/permissions`);
      setUserPermissions(Array.isArray(data?.permissions) ? data.permissions : []);
    } catch (_) {
      setUserPermissions([]);
    }
    setPermDialogOpen(true);
  };

  const togglePermission = (key) => {
    setUserPermissions(prev => togglePagePermissionSet(prev, permUser?.role, key));
  };

  const toggleFeaturePermission = (pageKey, featureKey) => {
    setUserPermissions(prev => toggleFeaturePermissionSet(prev, permUser?.role, pageKey, featureKey));
  };

  const toggleDialogPermission = (key) => {
    setFormPermissions(prev => togglePagePermissionSet(prev, form.role, key));
  };

  const toggleDialogFeaturePermission = (pageKey, featureKey) => {
    setFormPermissions(prev => toggleFeaturePermissionSet(prev, form.role, pageKey, featureKey));
  };

  const savePermissions = async () => {
    try {
      await fetchJson(`/api/system-users/${permUser.id}/permissions`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ permissions: userPermissions })
      });
      setSnack({ open: true, severity: 'success', message: 'Permissions updated' });
      setPermDialogOpen(false);
    } catch (e) {
      setSnack({ open: true, severity: 'error', message: e?.message || 'Failed to save permissions' });
    }
  };

  // ---------- Columns ----------
  const columns = useMemo(() => [
    {
      field: 'user',
      headerName: 'User',
      flex: 1,
      minWidth: 260,
      renderCell: (params) => {
        const row = params.row;
        const rc = getRoleConfig(row.role);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
            <Avatar
              src={row.avatarUrl || undefined}
              sx={{
                bgcolor: rc.color,
                width: 38,
                height: 38,
                fontSize: 14,
                fontWeight: 700
              }}
            >
              {getInitials(row.name)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap>{row.name}</Typography>
              <Typography variant="caption" color="text.secondary" noWrap>{row.email}</Typography>
            </Box>
          </Box>
        );
      }
    },
    {
      field: 'role',
      headerName: 'Role',
      width: 160,
      renderCell: (params) => {
        const rc = getRoleConfig(params.value);
        return (
          <Chip
            icon={rc.icon}
            label={rc.label}
            size="small"
            sx={{
              bgcolor: alpha(rc.color, 0.1),
              color: rc.color,
              fontWeight: 600,
              '& .MuiChip-icon': { color: rc.color }
            }}
          />
        );
      }
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => {
        const sc = STATUS_CONFIG[params.value] || STATUS_CONFIG.inactive;
        return (
          <Chip
            icon={sc.icon}
            label={sc.label}
            color={sc.color}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 500 }}
          />
        );
      }
    },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" color={params.value ? 'text.primary' : 'text.disabled'}>
          {params.value || '—'}
        </Typography>
      )
    },
    {
      field: 'lastLoginAt',
      headerName: 'Last Active',
      width: 140,
      renderCell: (params) => {
        const val = params.row?.lastLoginAt;
        const isRecent = val && (Date.now() - new Date(val).getTime()) < 24 * 60 * 60 * 1000;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {isRecent && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main', flexShrink: 0 }} />}
            <Typography variant="body2" color={val ? 'text.primary' : 'text.disabled'}>
              {timeAgo(val)}
            </Typography>
          </Box>
        );
      }
    },
    {
      field: 'createdAt',
      headerName: 'Joined',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {params.value ? new Date(params.value).toLocaleDateString() : '—'}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      width: 160,
      getActions: (params) => {
        const row = params.row;
        const isSA = row.role === 'SystemAdmin';
        const isSelf = row.id === currentUser?.id;
        const actions = [
          <GridActionsCellItem key="edit" icon={<Tooltip title="Edit"><Edit /></Tooltip>} label="Edit" onClick={() => openEdit(row)} disabled={(isSA && !isSelf) || !canManageUsersAccess} />,
          <GridActionsCellItem key="perms" icon={<Tooltip title="Permissions"><Security /></Tooltip>} label="Permissions" onClick={() => openPermissions(row)} disabled={!canManageUsersAccess} />,
          <GridActionsCellItem key="reset" icon={<LockReset />} label="Reset Password" onClick={() => resetPassword(row)} showInMenu disabled={isSA && !isSelf} />,
        ];
        if (!isSA) {
          if (row.status === 'active') {
            actions.push(<GridActionsCellItem key="suspend" icon={<Block />} label="Suspend" onClick={() => setStatus(row, 'suspended')} showInMenu disabled={!canManageUsersAccess} />);
            actions.push(<GridActionsCellItem key="deactivate" icon={<Cancel />} label="Deactivate" onClick={() => setStatus(row, 'inactive')} showInMenu disabled={!canManageUsersAccess} />);
          } else {
            actions.push(<GridActionsCellItem key="activate" icon={<CheckCircle />} label="Activate" onClick={() => setStatus(row, 'active')} showInMenu disabled={!canManageUsersAccess} />);
          }
          actions.push(<GridActionsCellItem key="delete" icon={<Delete color="error" />} label="Delete" onClick={() => askDelete(row)} showInMenu disabled={!canManageUsersAccess} />);
        }
        return actions;
      }
    }
  ], [currentUser, canManageUsersAccess]);

  // ---------- Summary Cards ----------
  const summaryCards = [
    { label: 'Total Users', value: stats.total, icon: <People />, color: theme.palette.primary.main, bg: alpha(theme.palette.primary.main, 0.08) },
    { label: 'Active', value: stats.active, icon: <CheckCircle />, color: theme.palette.success.main, bg: alpha(theme.palette.success.main, 0.08) },
    { label: 'Admins', value: stats.admins, icon: <AdminPanelSettings />, color: '#7c3aed', bg: alpha('#7c3aed', 0.08) },
    { label: 'Active Today', value: stats.recentlyActive, icon: <Visibility />, color: theme.palette.info.main, bg: alpha(theme.palette.info.main, 0.08) },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Users & Access Management</Typography>
          <Typography variant="body2" color="text.secondary">Manage system-level user accounts, roles, and permissions</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<Refresh />} variant="outlined" onClick={load} size="small">Refresh</Button>
          <Button startIcon={<PersonAdd />} variant="contained" onClick={openCreate} disabled={!canManageUsersAccess}>Add User</Button>
        </Stack>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {summaryCards.map((c) => (
          <Grid item xs={6} sm={3} key={c.label}>
            <Card sx={{ bgcolor: c.bg, border: 'none', boxShadow: 'none' }}>
              <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>{c.label}</Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ color: c.color }}>{c.value}</Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: alpha(c.color, 0.15), color: c.color, width: 44, height: 44 }}>
                    {c.icon}
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Filters */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <TextField
              size="small"
              placeholder="Search by name or email..."
              value={filters.search}
              onChange={(e) => { setPage(0); setFilters(f => ({ ...f, search: e.target.value })); }}
              sx={{ minWidth: 280 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search fontSize="small" color="action" /></InputAdornment>
              }}
            />
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel>Role</InputLabel>
              <Select
                label="Role"
                value={filters.role}
                onChange={(e) => { setPage(0); setFilters(f => ({ ...f, role: e.target.value })); }}
              >
                <MenuItem value=""><em>All Roles</em></MenuItem>
                {ROLE_OPTIONS.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={filters.status}
                onChange={(e) => { setPage(0); setFilters(f => ({ ...f, status: e.target.value })); }}
              >
                <MenuItem value=""><em>All</em></MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="suspended">Suspended</MenuItem>
              </Select>
            </FormControl>
            {(filters.role || filters.status || filters.search) && (
              <Button size="small" onClick={() => { setPage(0); setFilters({ role: '', status: '', search: '' }); }}>
                Clear Filters
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card>
        {loading && <LinearProgress />}
        <Box sx={{ height: 560, width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            rowCount={total}
            pagination
            paginationMode="server"
            page={page}
            pageSize={pageSize}
            onPageChange={(p) => setPage(p)}
            onPageSizeChange={(s) => { setPage(0); setPageSize(s); }}
            rowsPerPageOptions={[10, 20, 50, 100]}
            rowHeight={60}
            disableSelectionOnClick
            sx={{
              border: 'none',
              '& .MuiDataGrid-columnHeaders': {
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                fontWeight: 600,
              },
              '& .MuiDataGrid-row:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.02),
              }
            }}
          />
        </Box>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {editing ? <Edit color="primary" /> : <PersonAdd color="primary" />}
            <Typography variant="h6" fontWeight={600}>
              {editing ? 'Edit System User' : 'Create System User'}
            </Typography>
          </Box>
          {!editing && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              A temporary password will be generated and sent to the user's email.
            </Typography>
          )}
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          <Stack spacing={2.5}>
            <TextField
              label="Full Name"
              value={form.name}
              onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
              fullWidth
              required
              autoFocus
            />
            <TextField
              label="Email Address"
              type="email"
              value={form.email}
              onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
              fullWidth
              required
              InputProps={{
                startAdornment: <InputAdornment position="start"><Email fontSize="small" color="action" /></InputAdornment>
              }}
            />
            <TextField
              label="Phone Number"
              value={form.phone}
              onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
              fullWidth
              placeholder="+237..."
            />
            <FormControl fullWidth required>
              <InputLabel>Role</InputLabel>
              <Select
                label="Role"
                value={form.role}
                onChange={(e) => {
                  const nextRole = e.target.value;
                  setForm(f => ({ ...f, role: nextRole }));
                  setFormPermissions(getDefaultPermissions(nextRole));
                }}
              >
                {ROLE_OPTIONS.map(r => (
                  <MenuItem key={r.value} value={r.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 24, height: 24, bgcolor: alpha(r.color, 0.15), color: r.color }}>{r.icon}</Avatar>
                      {r.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={form.status}
                onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} sx={{ mb: 1.5 }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>Page & Feature Permissions</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Configure access for super-admin pages and the key actions within them.
                  </Typography>
                </Box>
                <Button size="small" onClick={() => setFormPermissions(getDefaultPermissions(form.role))}>
                  Reset to role defaults
                </Button>
              </Stack>
              {form.role === 'SystemAdmin' ? (
                <Alert severity="info" icon={<AdminPanelSettings />} sx={{ mb: 1 }}>
                  System Admins always have full access to all super-admin pages and features.
                </Alert>
              ) : null}
              <FormGroup>
                {SYSTEM_PAGES.map(pg => (
                  <Box key={pg.key} sx={{ mb: 1.25, pl: 0.25 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isPermissionSelected(formPermissions, pg.key, form.role)}
                          onChange={() => toggleDialogPermission(pg.key)}
                          disabled={form.role === 'SystemAdmin'}
                          color="primary"
                        />
                      }
                      label={<Typography variant="body2" fontWeight={600}>{pg.label}</Typography>}
                      sx={{ ml: 0 }}
                    />
                    <Box sx={{ pl: 4, pt: 0.25 }}>
                      <FormGroup>
                        {(pg.features || []).map(feature => (
                          <FormControlLabel
                            key={feature.key}
                            control={
                              <Checkbox
                                size="small"
                                checked={isPermissionSelected(formPermissions, feature.key, form.role)}
                                onChange={() => toggleDialogFeaturePermission(pg.key, feature.key)}
                                disabled={form.role === 'SystemAdmin'}
                                color="primary"
                              />
                            }
                            label={<Typography variant="caption" color="text.secondary">{feature.label}</Typography>}
                            sx={{ ml: 0, my: -0.25 }}
                          />
                        ))}
                      </FormGroup>
                    </Box>
                  </Box>
                ))}
              </FormGroup>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} startIcon={editing ? <Edit /> : <PersonAdd />}>
            {editing ? 'Update' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <ConfirmDeleteDialog
        open={deleteOpen}
        onClose={() => { setDeleteOpen(false); setDeleteTarget(null); }}
        onConfirm={confirmDelete}
        title="Delete System User"
        message={deleteTarget ? `Are you sure you want to permanently delete ${deleteTarget.name} (${deleteTarget.email})? This action cannot be undone.` : 'Delete this user?'}
      />

      {/* Permissions Dialog */}
      <Dialog open={permDialogOpen} onClose={() => setPermDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: alpha(getRoleConfig(permUser?.role).color, 0.15), color: getRoleConfig(permUser?.role).color, width: 36, height: 36 }}>
              <Security fontSize="small" />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={600}>Page Permissions</Typography>
              <Typography variant="body2" color="text.secondary">{permUser?.name} &mdash; {getRoleConfig(permUser?.role).label}</Typography>
            </Box>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {permUser?.role === 'SystemAdmin' ? (
            <Alert severity="info" icon={<AdminPanelSettings />} sx={{ mb: 2 }}>
              System Admins have unrestricted access to all pages by default.
            </Alert>
          ) : (
            <Typography variant="body2" color="text.secondary" mb={2}>
              Select which super-admin pages and in-page features this user can access.
            </Typography>
          )}
          <FormGroup>
            {SYSTEM_PAGES.map(pg => (
              <Box key={pg.key} sx={{ mb: 1.25, pl: 0.25 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isPermissionSelected(userPermissions, pg.key, permUser?.role)}
                      onChange={() => togglePermission(pg.key)}
                      disabled={permUser?.role === 'SystemAdmin'}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontWeight: isPermissionSelected(userPermissions, pg.key, permUser?.role) ? 600 : 400 }}>
                      {pg.label}
                    </Typography>
                  }
                  sx={{ ml: 0, mb: 0.25 }}
                />
                <Box sx={{ pl: 4, pt: 0.25 }}>
                  <FormGroup>
                    {(pg.features || []).map(feature => (
                      <FormControlLabel
                        key={feature.key}
                        control={
                          <Checkbox
                            size="small"
                            checked={isPermissionSelected(userPermissions, feature.key, permUser?.role)}
                            onChange={() => toggleFeaturePermission(pg.key, feature.key)}
                            disabled={permUser?.role === 'SystemAdmin'}
                            color="primary"
                          />
                        }
                        label={<Typography variant="caption" color="text.secondary">{feature.label}</Typography>}
                        sx={{ ml: 0, my: -0.25 }}
                      />
                    ))}
                  </FormGroup>
                </Box>
              </Box>
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setPermDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={savePermissions} disabled={permUser?.role === 'SystemAdmin'} startIcon={<Security />}>
            Save Permissions
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4500}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))} variant="filled" sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
